import { useState, useEffect } from "react";
import { useParams, Link, Navigate, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { MaskedLine, Overline, FadeUp } from "../components/Reveal";
import { SERVICES, PRODUCTS, PLATFORMS } from "../data/services";
import { api } from "../lib/api";

const EnquireForm = ({ productName }) => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/enquiries", { ...form, service: productName });
      toast.success("Enquiry sent — our product team will reply within one business day.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "h-12 w-full rounded-md border border-primary/10 bg-muted/50 px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

  return (
    <section id="enquire" className="py-20 lg:py-28 bg-white border-y border-primary/10" data-testid="product-enquire">
      <div className="mx-auto max-w-3xl px-6 lg:px-10">
        <FadeUp>
          <Overline>Enquire about {productName}</Overline>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight text-primary">
            Tell us what you need — we reply within one business day.
          </h2>
        </FadeUp>
        <FadeUp delay={0.15}>
          <form onSubmit={submit} data-testid="product-enquire-form" className="mt-10 space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <input data-testid="enquire-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
              <input data-testid="enquire-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Work email" className={inputCls} />
            </div>
            <textarea
              data-testid="enquire-message-input"
              required
              rows={4}
              value={form.message}
              onChange={set("message")}
              placeholder={`What would you like ${productName} to do for you?`}
              className="w-full rounded-md border border-primary/10 bg-muted/50 px-3 py-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            />
            <button
              type="submit"
              data-testid="enquire-submit-btn"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/20"
            >
              {submitting ? "Sending…" : "Send Enquiry"} <ArrowUpRight size={16} weight="bold" />
            </button>
          </form>
        </FadeUp>
      </div>
    </section>
  );
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const location = useLocation();
  const product = PRODUCTS.find((s) => s.slug === slug) || PLATFORMS.find((s) => s.slug === slug);
  const isPlatform = !!PLATFORMS.find((s) => s.slug === slug);
  const service = SERVICES.find((s) => s.slug === slug) || product;
  const related = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  useEffect(() => {
    if (location.hash === "#enquire") {
      setTimeout(() => document.getElementById("enquire")?.scrollIntoView({ behavior: "smooth" }), 400);
    }
  }, [location.hash, slug]);

  if (!service) return <Navigate to="/services" replace />;

  return (
    <>
      <section className="pt-32 lg:pt-40 pb-16" data-testid="service-detail-hero">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Link to={product ? "/" : "/services"} data-testid="service-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-accent transition-colors">
              <ArrowLeft size={16} /> {product ? "Back to Home" : "All Services"}
            </Link>
          </FadeUp>
          <div className="mt-8 grid gap-12 lg:grid-cols-12 items-end">
            <div className="lg:col-span-7">
              <FadeUp><Overline>{product ? (isPlatform ? "Our Platform — " : "Our Product — ") : ""}{service.tagline}</Overline></FadeUp>
              <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter text-primary leading-[0.95]">
                <MaskedLine delay={0.1}>{service.name}</MaskedLine>
              </h1>
              <FadeUp delay={0.35} className="mt-8 max-w-2xl">
                <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">{service.description}</p>
                <p className="mt-4 text-sm font-semibold text-primary leading-relaxed">
                  {product
                    ? "Built AI-native from the ground up — lean, fast, compliant and secure, at a fraction of what legacy elephant vendors charge."
                    : "Delivered AI-native — lean, fast, compliant and cost-effective. No AI coating, no legacy bloat, no year-long rollouts."}
                </p>
              </FadeUp>
            </div>
            <div className="lg:col-span-5">
              <FadeUp delay={0.3}>
                <div className="overflow-hidden rounded-3xl border border-primary/10 aspect-[4/3]">
                <div className="relative h-full w-full">
                  <img src={service.image} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
                  {service.video ? (
                    <video
                      poster={service.image}
                      autoPlay
                      muted
                      loop
                      playsInline
                      data-testid="product-detail-video"
                      className="relative h-full w-full object-cover"
                    >
                      <source src={service.video.replace(".mp4", ".webm")} type="video/webm" />
                      <source src={service.video} type="video/mp4" />
                    </video>
                  ) : null}
                </div>
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {isPlatform && (
        <section className="pb-20 lg:pb-24" data-testid="platform-universal-teaser">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <FadeUp>
              <div className="rounded-3xl border border-accent/25 bg-accent/5 p-8 lg:p-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
                <div className="max-w-3xl">
                  <Overline>Universal Anxya</Overline>
                  <h2 className="mt-3 font-display text-2xl sm:text-3xl font-medium tracking-tight text-primary">
                    Anxya Health is the first vertical of Anxya AI OS & PaaS.
                  </h2>
                  <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
                    The universal platform extends the same agent, workflow, software, compliance and marketplace architecture across every industry, government and nonprofit jurisdiction.
                  </p>
                </div>
                <Link
                  to="/universal-ai-os"
                  data-testid="platform-universal-link"
                  className="inline-flex shrink-0 items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/20"
                >
                  Explore Universal Platform <ArrowUpRight size={16} weight="bold" />
                </Link>
              </div>
            </FadeUp>
          </div>
        </section>
      )}

      <section className="py-20 lg:py-28 bg-white border-y border-primary/10" data-testid="service-capabilities">        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <span className="lg:sticky lg:top-32 block">
              <Overline>Capabilities</Overline>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight text-primary">What we deliver</h2>
            </span>
          </div>
          <div className="lg:col-span-8 grid gap-4 sm:grid-cols-2">
            {service.capabilities.map((cap, i) => (
              <FadeUp key={cap} delay={i * 0.05}>
                <div className="flex items-start gap-3 rounded-xl border border-primary/10 bg-background p-5 h-full">
                  <CheckCircle size={20} weight="fill" className="mt-0.5 shrink-0 text-accent" />
                  <p className="text-sm font-medium text-foreground leading-relaxed">{cap}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {service.tools && (
        <section className="py-20 lg:py-28" data-testid="platform-tools">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <FadeUp>
              <Overline>Solutions, tools, APIs & resources</Overline>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight text-primary">Everything inside the platform</h2>
            </FadeUp>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {service.tools.map((t, i) => (
                <FadeUp key={t.name} delay={i * 0.03}>
                  <a
                    href={service.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`platform-tool-${i}`}
                    className="block h-full rounded-xl border border-primary/10 bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:border-accent/50"
                  >
                    <p className="text-sm font-bold text-primary">{t.name}</p>
                    <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{t.desc}</p>
                  </a>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 lg:py-28 bg-primary text-white" data-testid="service-outcomes">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Overline>Our targets</Overline>
            <p className="mt-3 text-sm text-white/50 max-w-xl">The bar we hold ourselves to on every engagement — and report against, honestly.</p>
          </FadeUp>
          <div className="mt-10 grid gap-10 sm:grid-cols-3">
            {service.outcomes.map((o, i) => (
              <FadeUp key={o.label} delay={i * 0.1}>
                <p className="font-display text-5xl lg:text-6xl font-light tracking-tighter">{o.value}</p>
                <p className="mt-3 text-sm text-white/60 max-w-[16rem]">{o.label}</p>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.3} className="mt-14 flex flex-wrap gap-4">
            <Link
              to="/demo"
              data-testid="service-detail-demo-btn"
              className="inline-flex items-center gap-2 rounded-full bg-accent px-7 py-3.5 text-sm font-semibold text-primary transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/30"
            >
              Talk to Us <ArrowUpRight size={16} weight="bold" />
            </Link>
            {product && (
              <button
                onClick={() => document.getElementById("enquire")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="service-detail-enquire-btn"
                className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-accent"
              >
                Enquire about {service.name} <ArrowUpRight size={16} weight="bold" />
              </button>
            )}
            {product?.external && (
              <a
                href={service.website}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="service-detail-website-btn"
                className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-accent"
              >
                Visit {service.website.replace("https://", "")} <ArrowUpRight size={16} weight="bold" />
              </a>
            )}
          </FadeUp>
        </div>
      </section>

      {product && <EnquireForm productName={service.name} />}

      <section className="py-20 lg:py-28" data-testid="service-related">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp><Overline>Related services</Overline></FadeUp>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {related.map((s, i) => (
              <FadeUp key={s.slug} delay={i * 0.08}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`related-service-${s.slug}`}
                  className="group flex h-full flex-col justify-between rounded-2xl border border-primary/10 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-accent/50"
                >
                  <h3 className="font-display text-xl font-medium tracking-tight text-foreground">{s.name}</h3>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary group-hover:text-accent transition-colors">
                    Explore <ArrowUpRight size={16} weight="bold" />
                  </span>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
